import type { Capability, UiProject } from '@nightshift/ui-types';

import type { ViewName } from './router.ts';

type NavGroup = 'rail' | 'reports' | 'account';

export interface NavEntry {
  view: ViewName;
  hint: string;
  group: NavGroup;
  /** Optional capability that lights this workspace. Always-on product surfaces omit it. */
  capability?: Capability;
}

type CatalogEntry = Omit<NavEntry, 'view'> & { view: string };

const RAIL: CatalogEntry[] = [
  { view: 'board', hint: 'The pipeline as a live board', group: 'rail' },
  { view: 'review', hint: 'Tickets waiting for a person in Human Review, with their risk', group: 'rail' },
  { view: 'graph', hint: 'Milestones and dependency direction on a canvas', group: 'rail' },
  { view: 'agents', hint: 'Machines, live attempts and runner health', group: 'rail' },
  { view: 'ops', hint: 'Machine health, queue and spend', group: 'rail' },
  { view: 'runs', hint: 'Every attempt and sidecar job, with transcripts', group: 'rail' },
  { view: 'budgets', hint: 'Performance targets over time', group: 'rail', capability: 'budgets' },
  { view: 'settings', hint: 'Config, with provenance', group: 'account' },
  { view: 'help', hint: 'Learn and operate Nightshift', group: 'account' },
];

/**
 * Surviving diagnostic surfaces, grouped under Reports.
 *
 * Ordered by how often an operator reaches for them during an incident.
 */
const REPORTS: CatalogEntry[] = [
  { view: 'ops', hint: 'Machine health, queue and spend', group: 'reports' },
  { view: 'runs', hint: 'Every attempt and sidecar job, with transcripts', group: 'reports' },
  {
    view: 'budgets',
    hint: 'Performance targets over time',
    group: 'reports',
    capability: 'budgets',
  },
];

/**
 * Letters for the rail, digits for the Reports group.
 *
 * Surviving keys keep their meaning. Retired screens' letters stay dropped.
 */
export const KEYBOARD_VIEWS: Record<string, ViewName> = {
  b: 'board',
  t: 'tickets',
  a: 'agents',
  r: 'reports',
  s: 'settings',
  '1': 'board',
  '2': 'ops',
  '3': 'runs',
  g: 'budgets',
};

/**
 * Capabilities that are lit for navigation.
 *
 * A selected project uses its own arrangement. All-projects uses the union, so a software
 * fleet still sees capability-gated screens when any project has them. Unknown views (a sibling
 * worktree's route that is not on this checkout) never appear.
 */
export function litCapabilities(projects: readonly UiProject[], selectedSlug: string | null): Set<Capability> {
  const scoped =
    selectedSlug === null
      ? projects
      : projects.filter((project) => project.slug === selectedSlug);
  const lit = new Set<Capability>();
  for (const project of scoped) {
    for (const capability of project.capabilities) lit.add(capability);
  }
  return lit;
}

/** Capabilities that gate a nav entry on this checkout — the only values worth remembering. */
const GATING_CAPABILITIES: ReadonlySet<string> = new Set(
  [...RAIL, ...REPORTS].flatMap((entry) => (entry.capability ? [entry.capability as string] : [])),
);

export const NAV_CAPABILITIES_KEY = 'nightshift.nav-capabilities';

export interface NavPrefsStore {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

function navStore(): NavPrefsStore | null {
  try {
    if (typeof window === 'undefined') return null;
    return window.localStorage;
  } catch {
    return null;
  }
}

function scopeKey(selectedSlug: string | null): string {
  return selectedSlug ?? '';
}

/**
 * The capability set this browser last saw for a scope.
 *
 * The nav is gated on a snapshot, and the first paint has no snapshot. Painting the remembered
 * set first is a guess, but it is last snapshot's answer for the same scope, and the snapshot
 * corrects it on arrival.
 */
export function rememberedCapabilities(
  selectedSlug: string | null,
  store: NavPrefsStore | null = navStore(),
): Set<Capability> {
  if (!store) return new Set();
  try {
    const raw = store.getItem(NAV_CAPABILITIES_KEY);
    if (!raw) return new Set();
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return new Set();
    const list = (parsed as Record<string, unknown>)[scopeKey(selectedSlug)];
    if (!Array.isArray(list)) return new Set();
    return new Set(
      list.filter((item): item is Capability => typeof item === 'string' && GATING_CAPABILITIES.has(item)),
    );
  } catch {
    return new Set();
  }
}

export function rememberCapabilities(
  selectedSlug: string | null,
  capabilities: ReadonlySet<Capability>,
  store: NavPrefsStore | null = navStore(),
): void {
  if (!store) return;
  try {
    const raw = store.getItem(NAV_CAPABILITIES_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : {};
    const all = typeof parsed === 'object' && parsed !== null ? { ...(parsed as Record<string, unknown>) } : {};
    all[scopeKey(selectedSlug)] = [...capabilities].filter((item) => GATING_CAPABILITIES.has(item));
    store.setItem(NAV_CAPABILITIES_KEY, JSON.stringify(all));
  } catch {
    // A browser with storage denied simply keeps the one-frame gap.
  }
}

function keepKnown(
  entry: CatalogEntry,
  known: ReadonlySet<string>,
  capabilities: ReadonlySet<Capability>,
): entry is CatalogEntry & { view: ViewName } {
  if (!known.has(entry.view)) return false;
  if (entry.capability && !capabilities.has(entry.capability)) return false;
  return true;
}

export function navItems(
  knownViews: readonly ViewName[],
  capabilities: ReadonlySet<Capability>,
): { rail: NavEntry[]; reports: NavEntry[]; account: NavEntry[] } {
  const known = new Set<string>(knownViews);
  const rail = RAIL.filter((entry) => keepKnown(entry, known, capabilities)).map((entry) => ({
    ...entry,
    view: entry.view as ViewName,
  }));
  return {
    rail: rail.filter((entry) => entry.group !== 'account'),
    reports: REPORTS.filter((entry) => keepKnown(entry, known, capabilities)),
    account: rail.filter((entry) => entry.group === 'account'),
  };
}

export function navItemIsActive(item: NavEntry, view: ViewName): boolean {
  return item.view === view;
}
